import {
  ALL_NAV_ITEMS,
  ALL_ACTIONS,
  PERMISSIONS,
  type NavItem,
  type Action,
  type Role,
  type RoleEntity,
  type UserEntity,
  type RoleDto,
} from "@procureflow/shared-types";

// US-054 (ADR-017) — FE mirror of the backend role resolver
// (apps/api/src/roles/role-permissions.ts). Kept in sync as part of the
// MSW-matches-contract discipline: the roles handler maps store rows to
// RoleDto with these, and the permission engine expands System Admin the
// same way the API does.

export interface ResolvedPermissions {
  nav_items: NavItem[];
  actions: Action[];
}

/**
 * The effective permission set for a role. System Admin expands to every
 * NavItem + Action at read time (so a newly added screen is granted
 * automatically); a normal role returns its stored arrays.
 */
export function resolveRolePermissions(role: RoleEntity): ResolvedPermissions {
  if (role.is_system_admin) {
    return { nav_items: [...ALL_NAV_ITEMS], actions: [...ALL_ACTIONS] };
  }
  return { nav_items: role.nav_items, actions: role.actions };
}

/**
 * The permission set the live UI gates on (ADR-017 §3). The
 * session-fetched /me/permissions set is authoritative; until it loads
 * (or if the fetch fails) fall back to the static PERMISSIONS manifest
 * for a known legacy role so seeded roles gate identically to
 * pre-ADR-017. A custom/unknown role with nothing fetched denies by
 * default (empty) — better a momentary empty nav than a wrong grant.
 */
export function effectivePermissions(
  user: { role: string } | null,
  fetched: ResolvedPermissions | null,
): ResolvedPermissions {
  if (fetched) return fetched;
  if (user && user.role in PERMISSIONS) {
    const p = PERMISSIONS[user.role as Role];
    return { nav_items: [...p.navItems], actions: [...p.actions] };
  }
  return { nav_items: [], actions: [] };
}

/**
 * Map a RoleEntity store row to the RoleDto the API returns: resolved
 * permissions + a user_count computed by role name across `users`.
 */
export function roleToDto(role: RoleEntity, users: UserEntity[]): RoleDto {
  const { nav_items, actions } = resolveRolePermissions(role);
  return {
    id: role.id,
    name: role.name,
    nav_items,
    actions,
    is_system_admin: role.is_system_admin,
    user_count: users.filter((u) => u.role === role.name).length,
    created_at: role.created_at,
  };
}
